import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useVacation } from '../../contexts/VacationContext';
import { formatDurationFromMinutes } from '../../utils/TimeSlotUtils';
import VacationDetailsModal from './VacationDetailsModal';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDuration = (totalMinutes) => {
    if (totalMinutes < 60) {
        return `${totalMinutes} min`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
        return `${hours} h`;
    }

    return `${hours} h ${minutes} min`;
};

const convertTimeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
};

const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MonthlyCalendarView = ({
    selectedStaffInfo,
    currentDate,
    selections,
    holidays = [],
    onCellClick,
    selectedDepartmentId
}) => {
    const { t } = useTranslation('schedule');

    const {
        subscribeToVacations,
        unsubscribe,
        getVacationsForDate
    } = useVacation();

    const [selectedVacation, setSelectedVacation] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    // ========================================================================
    // VACATION SUBSCRIPTION
    // ========================================================================

    useEffect(() => {
        if (!selectedDepartmentId) return;

        const [year, month] = currentDate.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0, 23, 59, 59, 999);

        const unsubscribeFn = subscribeToVacations(startDate, endDate, [selectedDepartmentId]);

        return () => {
            if (unsubscribeFn) unsubscribeFn();
            unsubscribe();
        };
    }, [currentDate, selectedDepartmentId, subscribeToVacations, unsubscribe]);

    // ========================================================================
    // HELPER FUNCTIONS
    // ========================================================================

    const generateMonthDays = () => {
        const [year, month] = currentDate.split('-').map(Number);
        const monthIndex = month - 1;
        const firstDay = new Date(year, monthIndex, 1);
        const lastDay = new Date(year, monthIndex + 1, 0);

        const days = [];
        const currentDateObj = new Date(firstDay);

        while (currentDateObj <= lastDay) {
            days.push(new Date(currentDateObj));
            currentDateObj.setDate(currentDateObj.getDate() + 1);
        }

        return days;
    };

    const getSelectionForDay = (dateString) => {
        const daySelections = selections.filter(sel => sel.day === dateString);
        if (daySelections.length === 0) return null;

        let earliestStart = 24 * 60;
        let latestEnd = 0;

        daySelections.forEach(sel => {
            const start = convertTimeToMinutes(sel.startTime);
            const end = convertTimeToMinutes(sel.endTime);
            if (start < earliestStart) earliestStart = start;
            if (end > latestEnd) latestEnd = end;
        });

        const totalMinutes = latestEnd - earliestStart;
        const startHours = Math.floor(earliestStart / 60);
        const startMinutes = earliestStart % 60;
        const endHours = Math.floor(latestEnd / 60);
        const endMinutes = latestEnd % 60;

        return {
            startTime: `${startHours.toString().padStart(2, '0')}:${startMinutes.toString().padStart(2, '0')}`,
            endTime: `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`,
            totalMinutes
        };
    };

    const getVacationsForDay = (dateString) => {
        const date = new Date(dateString + 'T12:00:00');
        const allVacations = getVacationsForDate(date);
        if (selectedStaffInfo) {
            return allVacations.filter(v => v.staffId === selectedStaffInfo.id);
        }
        return allVacations;
    };

    const handleCellClick = (dateString, dayVacations) => {
        if (selectedStaffInfo && dayVacations.length === 1) {
            const vacation = dayVacations[0];
            if (vacation.staffId === selectedStaffInfo.id) {
                setSelectedVacation(vacation);
                setShowDetailsModal(true);
                return;
            }
        }

        if (onCellClick) {
            onCellClick(dateString);
        }
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedVacation(null);
    };

    // ========================================================================
    // RENDER DATA PREPARATION
    // ========================================================================

    const monthDays = generateMonthDays();

    // Day headers in Mon-Sun order (European calendar format)
    const dayNames = [
        t('monthly.dayHeaders.mon'),
        t('monthly.dayHeaders.tue'),
        t('monthly.dayHeaders.wed'),
        t('monthly.dayHeaders.thu'),
        t('monthly.dayHeaders.fri'),
        t('monthly.dayHeaders.sat'),
        t('monthly.dayHeaders.sun'),
    ];

    const firstDay = monthDays[0];
    const firstDayOfWeek = firstDay.getDay();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    const monthlyTotal = selections.reduce((total, sel) => total + sel.duration, 0);

    const today = new Date();
    const todayString = formatDateLocal(today);

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <div className="monthly-calendar-view">

            {/* Scrollable wrapper — enables horizontal scroll on narrow viewports
                so the 7-column grid never forces the page to overflow         */}
            <div className="monthly-calendar-grid-wrapper">

                {/* Day of week headers */}
                <div className="monthly-grid-header">
                    {dayNames.map(day => (
                        <div key={day} className="monthly-grid-day-name">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar grid */}
                <div className="monthly-grid-body">

                    {/* Empty offset cells */}
                    {Array.from({ length: offset }).map((_, i) => (
                        <div key={`empty-${i}`} className="monthly-day-cell--empty" />
                    ))}

                    {/* Day cells */}
                    {monthDays.map(date => {
                        const dateString = formatDateLocal(date);
                        const selection = getSelectionForDay(dateString);
                        const isToday = dateString === todayString;

                        const holiday = holidays.find(h => h.dateString === dateString) || null;
                        const isHoliday = !!holiday;

                        const dayVacations = getVacationsForDay(dateString);
                        const hasVacation = dayVacations.length > 0;

                        return (
                            <div
                                key={dateString}
                                className={[
                                    'monthly-day-cell',
                                    isToday                                    && 'monthly-day-cell--today',
                                    isHoliday                                  && 'monthly-day-cell--holiday',
                                    !isHoliday && hasVacation                  && 'monthly-day-cell--vacation',
                                    !isHoliday && !hasVacation && selection     && 'monthly-day-cell--selected',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleCellClick(dateString, dayVacations)}
                            >
                                {/* Day number */}
                                <div
                                    className={[
                                        'monthly-day-number',
                                        isToday              && 'monthly-day-number--today',
                                        isHoliday && !isToday && 'monthly-day-number--holiday',
                                    ].filter(Boolean).join(' ')}
                                >
                                    {date.getDate()}
                                </div>

                                {/* Holiday name badge */}
                                {isHoliday && holiday.name && (
                                    <div className="monthly-holiday-badge">
                                        {holiday.name}
                                    </div>
                                )}

                                {/* Vacation info */}
                                {hasVacation && (
                                    <div className="monthly-vacation-info">
                                        {dayVacations.slice(0, 4).map((vacation) => {
                                            const durationDisplay = formatDurationFromMinutes(
                                                vacation.durationMinutes || 0
                                            );
                                            return (
                                                <div
                                                    key={vacation.id}
                                                    className="monthly-vacation-entry"
                                                >
                                                    {getInitials(vacation.staffName)} &bull;{' '}
                                                    {t('monthly.vacation')} &bull;{' '}
                                                    {durationDisplay}
                                                </div>
                                            );
                                        })}
                                        {dayVacations.length > 4 && (
                                            <div className="monthly-vacation-more">
                                                {t('monthly.more', { count: dayVacations.length - 4 })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Selection info */}
                                {selection && (
                                    <div className="monthly-selection-info">
                                        <div className="monthly-selection-time">
                                            {selection.startTime} - {selection.endTime}
                                        </div>
                                        <div className="monthly-selection-duration">
                                            {formatDuration(selection.totalMinutes)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Monthly total summary */}
            {selections.length > 0 && (
                <div className="monthly-total-summary">
                    <div className="monthly-total-label">
                        {t('monthly.monthlyTotal')}
                    </div>
                    <div className="monthly-total-value">
                        {formatDuration(monthlyTotal)}
                    </div>
                </div>
            )}

            {/* Vacation Details Modal */}
            {showDetailsModal && selectedVacation && (
                <VacationDetailsModal
                    isOpen={showDetailsModal}
                    onClose={handleCloseDetailsModal}
                    vacation={selectedVacation}
                />
            )}
        </div>
    );
};

export default MonthlyCalendarView;